import { useMemo, useState } from "react";
import { C } from "../../shared/theme";
import { MonthCalendar } from "../../shared/MonthCalendar";
import { goalColorTokens } from "../../shared/goalColors";
import { formatDeadlineDate } from "../../shared/deadlines";

const sectionLabel = () => ({ fontSize: 12, fontWeight: 700, color: C.textMid, letterSpacing: "0.07em", textTransform: "uppercase" });

// The Goals page's Calendar view: active goals marked on a month by deadline, then the goals due (all, or on the
// day tapped), then the goals with no deadline. renderGoals(goals, { dated }) draws the goal rows, so they look
// as they do in the page's other views.
export function GoalCalendarView({ goals, renderGoals }) {
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD" | null
  const active = useMemo(() => (goals || []).filter(g => g.status === "active"), [goals]);
  const byDay = useMemo(() => {
    const map = {};
    active.forEach(g => { if (g.deadline) (map[g.deadline] ||= []).push(g); });
    return map;
  }, [active]);
  // One goal fills its day with the goal's colour; two or more use the accent.
  const marks = useMemo(() => Object.fromEntries(Object.entries(byDay).map(([day, gs]) => [day, {
    color: gs.length === 1 ? goalColorTokens(gs[0].color).main : C.accent,
    label: gs.map(g => g.title).join(", "),
  }])), [byDay]);
  const dated = useMemo(() => active.filter(g => g.deadline).sort((a, b) => a.deadline.localeCompare(b.deadline)), [active]);
  const undated = useMemo(() => active.filter(g => !g.deadline).sort((a, b) => (a.title || "").localeCompare(b.title || "")), [active]);
  const shown = selectedDay ? (byDay[selectedDay] || []) : dated;

  return (
    <>
      <MonthCalendar marks={marks} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
      {(selectedDay || dated.length > 0) && (
        <>
          <h2 style={{ ...sectionLabel(), margin: 0, padding: "0 0 8px" }}>{selectedDay ? `Due ${formatDeadlineDate(selectedDay)}` : "Goals with deadlines"}</h2>
          {shown.length > 0 ? renderGoals(shown, { dated: true })
            : <p style={{ margin: 0, fontSize: 15, color: C.textMid, padding: "4px 0 12px" }}>No goals are due that day.</p>}
        </>
      )}
      {undated.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: dated.length || selectedDay ? "16px 0 12px" : "0 0 12px" }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <h2 style={{ ...sectionLabel(), margin: 0, whiteSpace: "nowrap" }}>No deadline</h2>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
          {renderGoals(undated, { dated: false })}
        </>
      )}
    </>
  );
}
